import * as React from 'react';
import {
  Alert,
  AlertActionCloseButton,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Content,
  Flex,
  Gallery,
  GalleryItem,
  PageSection,
  SearchInput,
  Switch,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import {
  AngleLeftIcon,
  AngleRightIcon,
  QuestionCircleIcon,
} from '@patternfly/react-icons';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import { ACM_DARK_TOKENS, ACM_LIGHT_TOKENS } from '@app/ACMOpenShift/acmOverviewTokens';
import { CatalogSourceLabel } from '@app/components/CatalogSourceLabel';
import { StatusLabel } from '@app/components/StatusLabel';
import redHatLogo from '@app/bgimages/red-hat-logo.svg';
import ibmLogo from '@app/bgimages/ibm-logo.svg';
import {
  architectureFilterOptions,
  categoryFilterSections,
  infrastructureCategoryIds,
  mockSoftwareCatalogItems,
  operatingSystemCategoryIds,
  SoftwareCatalogItem,
} from '@app/data/softwareCatalogData';
import { TruncatedLabel } from '@app/components/TruncatedLabel';
import { useTheme } from '@app/utils/theme';
import './SoftwareCatalog.css';

const ITEMS_PER_PAGE = 8;

const matchesCategoryFilter = (itemCategories: string[], activeCategories: Set<string>): boolean => {
  if (activeCategories.size === 0) {
    return true;
  }

  if (itemCategories.some((category) => activeCategories.has(category))) {
    return true;
  }

  if (
    itemCategories.includes('infrastructure') &&
    infrastructureCategoryIds.some((category) => activeCategories.has(category))
  ) {
    return true;
  }

  if (
    itemCategories.includes('operating-systems') &&
    operatingSystemCategoryIds.some((category) => activeCategories.has(category))
  ) {
    return true;
  }

  return false;
};

const SoftwareCatalog: React.FunctionComponent = () => {
  const { isDark } = useTheme();
  const C = isDark ? ACM_DARK_TOKENS : ACM_LIGHT_TOKENS;

  const [searchValue, setSearchValue] = React.useState('');
  const [showInstalledOnly, setShowInstalledOnly] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [checkedCategories, setCheckedCategories] = React.useState<Set<string>>(
    () => new Set(['monitoring', 'rhel'])
  );
  const [checkedArchitectures, setCheckedArchitectures] = React.useState<Set<string>>(
    () => new Set(['x86_64', 'aarch64'])
  );

  const catalogStyle = React.useMemo(
    () =>
      ({
        '--catalog-bg': isDark ? C.s1 : '#ffffff',
        '--catalog-surface': isDark ? C.s1 : '#ffffff',
        '--catalog-surface-muted': C.s2,
        '--catalog-surface-elevated': C.s3,
        '--catalog-border': C.border,
        '--catalog-border-strong': C.border2,
        '--catalog-text-primary': C.t1,
        '--catalog-text-secondary': C.t2,
        '--catalog-text-muted': C.t3,
        '--catalog-text-subtle': C.t4,
        color: C.t1,
        backgroundColor: isDark ? C.s1 : '#ffffff',
      }) as React.CSSProperties,
    [C, isDark]
  );

  const toggleCategory = (id: string, checked: boolean) => {
    setCheckedCategories((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
    setPage(1);
  };

  const toggleArchitecture = (id: string, checked: boolean) => {
    setCheckedArchitectures((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
    setPage(1);
  };

  const filteredItems = React.useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const activeArchitectures = Array.from(checkedArchitectures);

    return mockSoftwareCatalogItems.filter((item) => {
      if (showInstalledOnly && !item.installed) {
        return false;
      }

      if (query && !item.name.toLowerCase().includes(query) && !item.provider.toLowerCase().includes(query)) {
        return false;
      }

      if (!matchesCategoryFilter(item.categories, checkedCategories)) {
        return false;
      }

      if (
        activeArchitectures.length > 0 &&
        !item.architectures.some((arch) => activeArchitectures.includes(arch))
      ) {
        return false;
      }

      return true;
    });
  }, [checkedArchitectures, checkedCategories, searchValue, showInstalledOnly]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const catalogLogos: Record<NonNullable<SoftwareCatalogItem['logo']>, string> = {
    redhat: redHatLogo,
    ibm: ibmLogo,
  };

  const renderCatalogCard = (item: SoftwareCatalogItem) => (
    <GalleryItem key={item.id} className="software-catalog__gallery-item">
      <Card className="software-catalog__card" isCompact isFullHeight isPlain>
        <CardHeader
          className="software-catalog__card-header"
          actions={{
            actions: <CatalogSourceLabel sourceType={item.sourceType} />,
            hasNoOffset: true,
          }}
        >
          <div className="software-catalog__card-intro">
            <img
              className="software-catalog__card-logo"
              src={catalogLogos[item.logo ?? 'redhat']}
              alt=""
            />
            <div className="software-catalog__card-heading">
              <CardTitle className="software-catalog__card-title">
                <TruncatedLabel content={item.name} />
              </CardTitle>
              <p className="software-catalog__card-provider">Provided by {item.provider}</p>
            </div>
          </div>
        </CardHeader>
        <CardBody className="software-catalog__card-body">
          <p className="software-catalog__card-description">{item.description}</p>
        </CardBody>
        <CardFooter className="software-catalog__card-footer">
          {item.updateAvailable ? (
            <StatusLabel status="info">
              <span className="pf-v6-screen-reader">Status: </span>
              Update available
            </StatusLabel>
          ) : item.installed ? (
            <StatusLabel status="success">
              <span className="pf-v6-screen-reader">Status: </span>
              Installed
            </StatusLabel>
          ) : null}
        </CardFooter>
      </Card>
    </GalleryItem>
  );

  return (
    <>
      <PageSection hasBodyWrapper={false} className="acm-page__header-section">
        <div className="acm-page__header-inner">
          <Flex
            justifyContent={{ default: 'justifyContentSpaceBetween' }}
            alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          >
            <PageHeaderTitle
              title="Software catalog"
              description="Browse, filter, and manage software sources for your edge fleet."
            />
            <PageContextActions placement="toolbar" />
          </Flex>
        </div>
      </PageSection>

      <PageSection hasBodyWrapper={false} className="acm-page__content-section">
        <div className="software-catalog software-catalog__canvas acm-page__canvas acm-page__panel" style={catalogStyle}>
      {showAlert && (
        <Alert
          variant="info"
          isInline
          className="app-info-alert"
          title="New operating systems are available"
          actionClose={<AlertActionCloseButton onClose={() => setShowAlert(false)} />}
        >
          Enhanced with agentic AI for better security.
        </Alert>
      )}
      <div className="software-catalog__toolbar">
        <Toolbar>
          <ToolbarContent className="software-catalog__toolbar-content">
            <ToolbarGroup align={{ default: 'alignStart' }}>
              <ToolbarItem>
                <SearchInput
                  className="app-search-input"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(_event, value) => {
                    setSearchValue(value);
                    setPage(1);
                  }}
                  onClear={() => {
                    setSearchValue('');
                    setPage(1);
                  }}
                />
              </ToolbarItem>
              <ToolbarItem>
                <Button variant="primary">Add source</Button>
              </ToolbarItem>
              <ToolbarItem>
                <Button variant="secondary" className="software-catalog__btn-manage">
                  Manage sources
                </Button>
              </ToolbarItem>
              <ToolbarItem>
                <Switch
                  id="show-installed-only"
                  label="Show installed only"
                  isChecked={showInstalledOnly}
                  onChange={(_event, checked) => {
                    setShowInstalledOnly(checked);
                    setPage(1);
                  }}
                />
              </ToolbarItem>
            </ToolbarGroup>
            <ToolbarGroup align={{ default: 'alignEnd' }}>
              <ToolbarItem>
                <div className="software-catalog__pagination">
                  <Button
                    variant="plain"
                    aria-label="Previous page"
                    isDisabled={currentPage <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    <AngleLeftIcon />
                  </Button>
                  <Button
                    variant="plain"
                    aria-label="Next page"
                    isDisabled={currentPage >= totalPages}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  >
                    <AngleRightIcon />
                  </Button>
                </div>
              </ToolbarItem>
            </ToolbarGroup>
          </ToolbarContent>
        </Toolbar>
      </div>

      <div className="software-catalog__body">
        <aside className="software-catalog__filters" aria-label="Catalog filters">
          <div className="software-catalog__filter-group">
            <div className="software-catalog__filter-label">
              <span>Category</span>
              <Button variant="plain" className="software-catalog__help-btn" aria-label="Category filter help">
                <QuestionCircleIcon />
              </Button>
            </div>
            {categoryFilterSections.map((section) => (
              <div key={section.id} className="software-catalog__filter-section">
                <div className="software-catalog__filter-section-title">{section.label}</div>
                <div className="software-catalog__filter-options">
                  {section.options.map((option) => (
                    <Checkbox
                      key={option.id}
                      id={`category-${option.id}`}
                      className="software-catalog__filter-checkbox"
                      label={option.label}
                      isChecked={checkedCategories.has(option.id)}
                      onChange={(_event, checked) => toggleCategory(option.id, checked)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="software-catalog__filter-group">
            <div className="software-catalog__filter-label">
              <span>Architecture</span>
              <Button variant="plain" className="software-catalog__help-btn" aria-label="Architecture filter help">
                <QuestionCircleIcon />
              </Button>
            </div>
            <div className="software-catalog__filter-options">
              {architectureFilterOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  id={`architecture-${option.id}`}
                  className="software-catalog__filter-checkbox"
                  label={option.label}
                  description={option.support}
                  isChecked={checkedArchitectures.has(option.id)}
                  onChange={(_event, checked) => toggleArchitecture(option.id, checked)}
                />
              ))}
            </div>
          </div>
        </aside>

        <div className="software-catalog__content">
          <Gallery className="software-catalog__gallery" hasGutter minWidths={{ default: '250px' }}>
            {paginatedItems.map(renderCatalogCard)}
          </Gallery>
        </div>
      </div>
        </div>
      </PageSection>
    </>
  );
};

export { SoftwareCatalog };
